import type { Partner } from 'src/services/partner';
import type { SaleOrder, SaleOrderReadExt, SaleOrderUpdateExt } from 'src/services/saleOrder';
import { validateEmail } from 'src/utils/general';

// Extracted out of OutboundOverlay.tsx (pure logic, no component state) so it
// can be unit-tested without rendering the overlay — that component pulls in
// Chat/ProductTable/ShippingLines/... and, transitively, `src/services/api`
// (openapi-fetch, ESM-only, unparsable by CRA's Jest transform). Only
// `import type` bindings are used here for anything from `src/services/*`, so
// this module has no runtime dependency on that chain.

export const validateOutboundPartner = (partner: Partner, requireEmail = true): boolean => {
  return !(
    !partner.name ||
    !partner.address?.country_code ||
    !partner.address?.street ||
    !partner.address?.zip_code ||
    !partner.address?.city ||
    (requireEmail && (!partner.email || !validateEmail(partner.email)))
  );
};

// Save-button gate for the edit overlay. Mirrors CreateSaleOrderOverlay's
// validateEntry(), but structured as a chain of mutually-exclusive branches
// keyed on which single top-level field changed first (name / carrier+
// warehouse / partner / order_lines / is_self_collection / address_confirmed
// / incoterm_id / is_cod+cod_amount+cod_currency_id) — a pre-existing design
// this function preserves rather than redesigns. Each branch validates the
// MERGED (patch-over-original) state, never just the raw patch, so an
// explicit `null` written by a "clear on toggle-off" handler is honoured
// instead of falling back to a stale original value (`!== undefined`, not
// `??`).
// `saleOrderItem` is declared `Partial<...>` (not `SaleOrderUpdateExt`) because
// it genuinely IS a partial patch at runtime — `useOutboundOrder`'s state only
// ever holds the fields the user actually touched (built up incrementally via
// `updateSaleOrderItem`), even though the hook's own `useState<SaleOrderUpdateExt>`
// declaration is the (looser, `as any`-escaped) full-record type.
export const validateOutboundEntry = (
  saleOrderItem: Partial<SaleOrderUpdateExt>,
  saleOrder: SaleOrderReadExt | undefined,
): boolean => {
  if (!saleOrderItem || !saleOrder || !saleOrder.warehouse || !saleOrder.partner) return false;

  // Property access rather than destructuring throughout (`saleOrderItem.carrier_id`,
  // not `const { carrier_id } = saleOrderItem`) — the API-shaped snake_case field
  // names trip the repo's `naming-convention` lint rule when bound as local
  // identifiers, but not when read as object properties.
  if (saleOrderItem.name !== undefined) {
    return (
      saleOrderItem.name != null &&
      saleOrderItem.name.trim() !== '' &&
      saleOrderItem.name !== saleOrder.name &&
      !!saleOrder.warehouse.id &&
      validateOutboundPartner(saleOrder.partner)
    );
  }
  // carrier_id: null means the carrier was cleared via the X, which is a change
  // only if the order had a carrier
  const carrierChanged =
    saleOrderItem.carrier_id !== undefined && (saleOrder.carrier?.id ?? null) !== saleOrderItem.carrier_id;
  if (carrierChanged || saleOrderItem.carrier_id || saleOrderItem.warehouse_id) {
    const warehouseChanged = !!saleOrderItem.warehouse_id && saleOrder.warehouse.id !== saleOrderItem.warehouse_id;
    return (
      (carrierChanged || warehouseChanged) && !!saleOrder.warehouse.id && validateOutboundPartner(saleOrder.partner)
    );
  }
  if (saleOrderItem.partner) {
    return !!saleOrder.warehouse.id && validateOutboundPartner(saleOrderItem.partner as Partner);
  }
  if (saleOrderItem.order_lines) {
    return (
      saleOrderItem.order_lines.length > 0 && !!saleOrder.warehouse.id && validateOutboundPartner(saleOrder.partner)
    );
  }
  if (saleOrderItem.is_self_collection !== undefined) {
    return (
      saleOrder.is_self_collection !== saleOrderItem.is_self_collection &&
      !!saleOrder.warehouse.id &&
      validateOutboundPartner(saleOrder.partner)
    );
  }
  // Confirming the address does not touch the recipient, and the address form is
  // locked meanwhile — so a recipient imported without email must not block this
  // save. The address itself still has to be complete: the confirmation only
  // vouches for e.g. a missing house number.
  if (saleOrderItem.address_confirmed !== undefined && saleOrderItem.address_confirmed !== null) {
    return (
      (saleOrder as SaleOrder).address_confirmed !== saleOrderItem.address_confirmed &&
      !!saleOrder.warehouse.id &&
      validateOutboundPartner(saleOrder.partner, false)
    );
  }
  if (saleOrderItem.order_note !== undefined) {
    // '' and a missing note are the same thing: the textarea renders a missing
    // note as an empty string, so typing and deleting again is not a change
    return (
      (saleOrderItem.order_note || '') !== (saleOrder.order_note || '') &&
      !!saleOrder.warehouse.id &&
      validateOutboundPartner(saleOrder.partner)
    );
  }
  // incoterm_id is optional on the backend — any change is save-worthy on its own,
  // no "is it actually different / is it valid" check needed.
  if (saleOrderItem.incoterm_id !== undefined) {
    return !!saleOrder.warehouse.id && validateOutboundPartner(saleOrder.partner);
  }
  if (
    saleOrderItem.is_cod !== undefined ||
    saleOrderItem.cod_amount !== undefined ||
    saleOrderItem.cod_currency_id !== undefined
  ) {
    const mergedIsCod = saleOrderItem.is_cod !== undefined ? saleOrderItem.is_cod : saleOrder.is_cod ?? false;
    if (!mergedIsCod) {
      return !!saleOrder.warehouse.id && validateOutboundPartner(saleOrder.partner);
    }
    const mergedAmount =
      saleOrderItem.cod_amount !== undefined ? saleOrderItem.cod_amount : saleOrder.cod_amount ?? null;
    const mergedCurrencyId =
      saleOrderItem.cod_currency_id !== undefined ? saleOrderItem.cod_currency_id : saleOrder.cod_currency_id ?? null;
    return (
      !!mergedAmount &&
      mergedAmount > 0 &&
      !!mergedCurrencyId &&
      !!saleOrder.warehouse.id &&
      validateOutboundPartner(saleOrder.partner)
    );
  }

  return false;
};
